import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const DocumentUpload = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Get user info from previous step
    const storedInfo = sessionStorage.getItem('userInfo');
    if (!storedInfo) {
      navigate('/');
      return;
    }
    setUserInfo(JSON.parse(storedInfo));
    
    // Initialize with one empty slot
    if (files.length === 0) {
      setFiles([null]);
    }
  }, [navigate, files.length]);

  const isValidFileType = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf'
    ];
    return allowedTypes.includes(file.type);
  };

  const getFileIcon = (file) => {
    if (file.type === 'application/pdf') {
      return '📄';
    } else if (file.type.startsWith('image/')) {
      return '🖼️';
    }
    return '📎';
  };

  const handleFileSelect = (index, file) => {
    if (file && isValidFileType(file)) {
      // Check file size limits
      const maxSize = file.type === 'application/pdf' ? 25 * 1024 * 1024 : 10 * 1024 * 1024; // 25MB for PDF, 10MB for images
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        setError(`File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size (${maxSizeMB}MB) for ${file.type === 'application/pdf' ? 'PDF documents' : 'images'}.`);
        return;
      }

      const newFiles = [...files];
      if (index < newFiles.length) {
        newFiles[index] = file;
      } else {
        newFiles.push(file);
      }
      setFiles(newFiles);
      setError('');
    } else {
      setError('Please select a valid file type: images (JPEG, PNG) or PDF documents');
    }
  };

  const addFileSlot = () => {
    if (files.length < 3) {
      setFiles([...files, null]);
    }
  };

  const removeFileSlot = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    // Update refs array
    fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
  };

  const handleFileInputChange = (index) => (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(index, file);
    }
  };

  const handleFileCapture = (index) => {
    // For mobile devices, this will open the camera for images or file picker for PDFs
    const input = fileInputRefs.current[index];
    if (input) {
      input.click();
    }
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles[index] = null;
    setFiles(newFiles);
  };

  const canSubmit = () => {
    const validFiles = files.filter(file => file !== null);
    return validFiles.length > 0 && !uploading;
  };

  const handleSubmit = async () => {
    const validFiles = files.filter(file => file !== null);
    if (validFiles.length === 0) {
      setError('Please select at least 1 document before submitting');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', userInfo.name);
      formData.append('account_number', userInfo.accountNumber);
      
      // Only append non-null files
      validFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await axios.post('/api/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      if (response.status === 200) {
        // Store submission ID for thank you page
        sessionStorage.setItem('submissionId', response.data.submission_id);
        navigate('/thank-you');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(
        error.response?.data?.detail || 
        'Upload failed. Please check your connection and try again.'
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (!userInfo) {
    return <div>Loading...</div>;
  }

  return (
    <div className="form-container image-upload-container">
      <h2>Document Upload</h2>
      <div className="upload-info">
        <p>
          Upload documents for <strong>{userInfo.name}</strong> (Account: <strong>{userInfo.accountNumber}</strong>)
          <br />
          <strong>{files.filter(file => file !== null).length}</strong> file(s) selected • Maximum 3 files allowed
          <br />
          <small>Supported formats: Images (JPEG, PNG) up to 10MB, PDF documents up to 25MB</small>
        </p>
      </div>

      {error && (
        <div className="status-message status-error">
          {error}
        </div>
      )}

      <div className="upload-grid">
        {files.map((file, index) => (
          <div
            key={index}
            className={`upload-slot ${file ? 'has-file' : ''}`}
            onClick={() => !uploading && handleFileCapture(index)}
          >
            {file ? (
              <>
                {file.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Upload ${index + 1}`}
                  />
                ) : (
                  <div className="file-preview">
                    <span className="file-icon">{getFileIcon(file)}</span>
                    <div className="file-info">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">{(file.size / (1024 * 1024)).toFixed(1)}MB</span>
                      <span className="file-type">{file.type === 'application/pdf' ? 'PDF' : 'Document'}</span>
                    </div>
                  </div>
                )}
                {!uploading && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="remove-file-btn"
                      title="Remove file"
                    >
                      ×
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFileSlot(index);
                      }}
                      className="delete-slot-btn"
                      title="Delete slot"
                    >
                      🗑️
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="upload-slot-content">
                <span className="icon">📷</span>
                <span className="text">
                  Document {index + 1}
                  <small>Tap to capture photo or select file</small>
                </span>
                {!uploading && files.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFileSlot(index);
                    }}
                    className="delete-empty-slot-btn"
                    title="Remove slot"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            
            <input
              ref={el => fileInputRefs.current[index] = el}
              type="file"
              accept="image/*,.pdf"
              capture="environment"
              onChange={handleFileInputChange(index)}
              className="hidden-input"
              disabled={uploading}
            />
          </div>
        ))}
        
        {/* Add new slot button */}
        {!uploading && files.length < 4 && (
          <div 
            className="upload-slot add-slot"
            onClick={addFileSlot}
          >
            <div className="upload-slot-content">
              <span className="icon">📷➕</span>
              <span className="text">
                Add Slot
                <small>Tap to add another photo slot</small>
              </span>
            </div>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-progress">
        <div className="status-message status-info">
          Uploading documents to secure storage... {uploadProgress}%
        </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="button-group">
        <button
          type="button"
          onClick={handleBack}
          className="btn btn-secondary"
          disabled={uploading}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="btn btn-primary"
          disabled={!canSubmit()}
        >
          {uploading ? 'Processing...' : 'Submit Documents →'}
        </button>
      </div>
    </div>
  );
};

export default DocumentUpload;
